import React, { useState, useEffect, useRef } from "react";
import { DayPicker } from "react-day-picker";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { CalendarIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Input } from "./input";

const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;

function PopoverContent({ className, align = "start", sideOffset = 4, ref, ...props }) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-auto rounded-2xl border border-gray-200 bg-white p-0 shadow-dropdown",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2",
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

function formatDate(date) {
  if (!date) return "";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function parseDate(str) {
  if (!str) return undefined;
  const parts = str.split("/");
  if (parts.length === 3) {
    const [day, month, year] = parts;
    const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (!isNaN(d.getTime())) return d;
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? undefined : d;
}

const DatePicker = ({ value, onChange, placeholder = "DD/MM/YYYY", disabled, error, autoFocus }) => {
  const [open, setOpen] = useState(false);
  // Local text mirrors what the user sees/types. It is kept in sync with the
  // incoming value, but never while the input is focused, so live typing (and
  // Cypress .type()) is not clobbered by the value round-tripping through the parent.
  const [text, setText] = useState(value || "");
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setText(value || "");
  }, [value]);

  const selectedDate = parseDate(value);

  const handleSelect = (date) => {
    if (date) {
      const formatted = formatDate(date);
      setText(formatted);
      onChange(formatted);
    }
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        type="text"
        className="flex-1"
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        error={error}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onBlur={() => {
          focusedRef.current = false;
          setText(value || "");
        }}
        onChange={(e) => {
          setText(e.target.value);
          onChange(e.target.value);
        }}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className={cn("shrink-0", error && "border-danger-900")}
            disabled={disabled}
            type="button"
            aria-label="Open calendar"
          >
            <CalendarIcon className="h-4 w-4 opacity-70" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end">
        <DayPicker
          mode="single"
          selected={selectedDate}
          defaultMonth={selectedDate}
          onSelect={handleSelect}
          autoFocus
          classNames={{
            months: "flex flex-col sm:flex-row gap-4 p-3 relative",
            month: "space-y-4",
            month_caption: "flex justify-center pt-1 relative items-center h-7",
            caption_label: "text-sm font-medium text-primary-900",
            nav: "absolute top-3 inset-x-1 flex items-center justify-between pointer-events-none",
            button_previous: "h-7 w-7 opacity-50 hover:opacity-100 rounded border border-gray-300 inline-flex items-center justify-center pointer-events-auto",
            button_next: "h-7 w-7 opacity-50 hover:opacity-100 rounded border border-gray-300 inline-flex items-center justify-center pointer-events-auto",
            chevron: "h-4 w-4 fill-primary-900",
            month_grid: "w-full border-collapse",
            weekdays: "flex",
            weekday: "text-gray-500 w-9 font-normal text-[0.8rem]",
            week: "flex w-full mt-2",
            day: "h-9 w-9 text-center text-sm p-0 relative",
            day_button: "h-9 w-9 p-0 font-normal rounded-md inline-flex items-center justify-center hover:bg-primary-900/10 hover:text-primary-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-900/40",
            selected: "[&>button]:bg-primary-900 [&>button]:text-on-primary [&>button]:hover:bg-primary-800 [&>button]:hover:text-on-primary",
            today: "[&>button]:bg-primary-900/10 [&>button]:text-primary-900 [&>button]:font-semibold",
            outside: "[&>button]:text-gray-400 [&>button]:opacity-50",
            disabled: "[&>button]:text-gray-400 [&>button]:opacity-50 [&>button]:pointer-events-none",
            hidden: "invisible",
          }}
        />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export { DatePicker };
